import React, { useState } from "react";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PieChartOutlineRoundedIcon from "@mui/icons-material/PieChartOutlineRounded";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

export interface ManagedStore {
  user_uuid: string;
  username: string;
  email?: string;
  shop_id?: string;
  shop_number?: string;
  shop_region?: string;
  login_disabled?: boolean;
}

export interface StoreCredentialResult {
  username: string;
  password: string;
  email_sent?: boolean;
}

interface StoreManagementPanelProps {
  stores: ManagedStore[];
  onAdd: (count: number) => Promise<void> | void;
  onToggleLogin: (store: ManagedStore, disabled: boolean) => Promise<void> | void;
  adding?: boolean;
  updatingId?: string;
  credentials?: StoreCredentialResult[] | null;
  onCloseCredential: () => void;
  compact?: boolean;
  onViewReports?: (store: ManagedStore) => void;
}

const downloadCredentialCsv = (credentials: StoreCredentialResult[]) => {
  const escape = (value: string) => `"${String(value).replace(/"/g, '""')}"`;
  const csv = [
    ["Role", "Username", "Password"],
    ...credentials.map((credential) => ["Store Manager", credential.username, credential.password]),
  ]
    .map((row) => row.map(escape).join(","))
    .join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `store_credentials_${credentials.length}_stores.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const StoreManagementPanel = ({
  stores,
  onAdd,
  onToggleLogin,
  adding = false,
  updatingId,
  credentials,
  onCloseCredential,
  compact = false,
  onViewReports,
}: StoreManagementPanelProps) => {
  const [confirmStore, setConfirmStore] = useState<ManagedStore | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [storeCount, setStoreCount] = useState(1);
  const activeStores = stores.filter((store) => !store.login_disabled).length;

  return (
    <>
      <Paper variant="outlined" sx={{ borderRadius: "18px", borderColor: "#E7EBF2", overflow: "hidden" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} p={compact ? 2 : 2.5} bgcolor="#FAFBFE">
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: "#EEF2FF", color: "primary.main", display: "grid", placeItems: "center" }}>
              <StorefrontRoundedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography fontSize={16} fontWeight={800}>Stores</Typography>
              <Typography fontSize={12} color="text.secondary">{activeStores} active · {stores.length} total</Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={adding ? <CircularProgress size={15} color="inherit" /> : <AddRoundedIcon />}
            onClick={() => setAddDialogOpen(true)}
            disabled={adding}
            sx={{ borderRadius: "11px", textTransform: "none", fontWeight: 800, boxShadow: "none" }}
          >
            Add Store
          </Button>
        </Box>

        <Divider />

        {stores.length === 0 ? (
          <Box textAlign="center" py={5} px={2}>
            <StorefrontRoundedIcon sx={{ fontSize: 34, color: "text.disabled" }} />
            <Typography fontSize={14} fontWeight={700} mt={1}>No stores yet</Typography>
            <Typography fontSize={12} color="text.secondary" mt={0.5}>Create the first store login for this company.</Typography>
          </Box>
        ) : (
          <Stack divider={<Divider />}>
            {stores.map((store) => {
              const disabled = Boolean(store.login_disabled);
              const updating = updatingId === store.user_uuid;
              return (
                <Box key={store.user_uuid} display="flex" alignItems="center" justifyContent="space-between" gap={2} px={compact ? 2 : 2.5} py={1.6}>
                  <Box minWidth={0}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography fontSize={13.5} fontWeight={800} noWrap>{store.username}</Typography>
                      <Chip
                        size="small"
                        label={disabled ? "Login disabled" : "Active"}
                        color={disabled ? "default" : "success"}
                        variant={disabled ? "outlined" : "filled"}
                        sx={{ height: 21, fontSize: 10.5, fontWeight: 800 }}
                      />
                    </Stack>
                    <Typography fontSize={12} color="text.secondary" noWrap mt={0.35}>
                      {store.shop_region || store.shop_number || store.shop_id || "Store profile pending"}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1}>
                    {onViewReports && (
                      <Tooltip title="View reports">
                        <IconButton
                          onClick={() => onViewReports(store)}
                          color="primary"
                          sx={{ border: "1px solid #E2E8F0", borderRadius: "10px" }}
                        >
                          <PieChartOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={disabled ? "Enable store login" : "Disable login (data is retained)"}>
                      <span>
                        <IconButton
                          disabled={updating}
                          onClick={() => disabled ? onToggleLogin(store, false) : setConfirmStore(store)}
                          color={disabled ? "success" : "default"}
                          sx={{ border: "1px solid #E2E8F0", borderRadius: "10px" }}
                        >
                          {updating ? <CircularProgress size={18} /> : disabled ? <CheckCircleRoundedIcon fontSize="small" /> : <BlockRoundedIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>

      <Dialog open={addDialogOpen} onClose={() => !adding && setAddDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
        <DialogTitle fontWeight={900}>Add stores</DialogTitle>
        <DialogContent>
          <Typography fontSize={13.5} color="text.secondary" mb={2}>
            How many store accounts would you like to create?
          </Typography>
          <Alert severity="info" sx={{ borderRadius: "12px", mb: 2 }}>
            Each active store is charged separately at the per-store rate of your current plan. Your subscription total will be updated for the next billing cycle.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label="Number of stores"
            value={storeCount}
            onChange={(event) => setStoreCount(Number(event.target.value))}
            inputProps={{ min: 1, max: 50, step: 1 }}
            error={!Number.isInteger(storeCount) || storeCount < 1 || storeCount > 50}
            helperText="Enter a number between 1 and 50."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button disabled={adding} onClick={() => setAddDialogOpen(false)} color="inherit" sx={{ textTransform: "none", fontWeight: 700 }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={adding || !Number.isInteger(storeCount) || storeCount < 1 || storeCount > 50}
            startIcon={adding ? <CircularProgress size={15} color="inherit" /> : <AddRoundedIcon />}
            onClick={async () => {
              await onAdd(storeCount);
              setAddDialogOpen(false);
              setStoreCount(1);
            }}
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: "10px" }}
          >
            {adding ? "Creating..." : `Create ${storeCount} ${storeCount === 1 ? "store" : "stores"}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmStore)} onClose={() => setConfirmStore(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
        <DialogTitle fontWeight={900}>Disable this store login?</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ borderRadius: "12px", mb: 2 }}>
            No store data will be deleted. Reports, employees, schedules and history remain available.
          </Alert>
          <Typography fontSize={13.5} color="text.secondary">
            <strong>{confirmStore?.username}</strong> will be signed out and unable to log in until an administrator enables it again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmStore(null)} color="inherit" sx={{ textTransform: "none", fontWeight: 700 }}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={async () => {
              if (confirmStore) await onToggleLogin(confirmStore, true);
              setConfirmStore(null);
            }}
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: "10px" }}
          >
            Disable Login
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(credentials?.length)} onClose={onCloseCredential} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
        <DialogTitle fontWeight={900}>{credentials?.length === 1 ? "Store created successfully" : `${credentials?.length || 0} stores created successfully`}</DialogTitle>
        <DialogContent>
          <Alert severity={credentials?.every((credential) => credential.email_sent) ? "success" : "warning"} sx={{ borderRadius: "12px", mb: 2.5 }}>
            {credentials?.every((credential) => credential.email_sent)
              ? "Credentials were emailed to the company contact."
              : "The stores were created, but email delivery failed. Download the credentials now."}
          </Alert>
          <Box sx={{ p: 2.5, borderRadius: "14px", bgcolor: "#17235C", color: "#fff" }}>
            <Typography fontSize={11} fontWeight={800} sx={{ color: "#AFC0FF", textTransform: "uppercase", letterSpacing: ".08em" }}>Store credentials</Typography>
            <Stack spacing={1.5} mt={2} maxHeight={300} sx={{ overflowY: "auto" }}>
              {credentials?.map((credential, index) => (
                <Box key={credential.username} pb={index < credentials.length - 1 ? 1.5 : 0} sx={{ borderBottom: index < credentials.length - 1 ? "1px solid rgba(255,255,255,.12)" : "none" }}>
                  <Typography fontSize={11} sx={{ color: "rgba(255,255,255,.62)" }}>Store {index + 1}</Typography>
                  <Typography fontSize={14} fontWeight={800}>{credential.username}</Typography>
                  <Typography fontSize={14} fontWeight={800} fontFamily="monospace">{credential.password}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onCloseCredential} color="inherit" sx={{ textTransform: "none", fontWeight: 700 }}>Close</Button>
          <Button
            variant="contained"
            startIcon={<DownloadRoundedIcon />}
            onClick={() => credentials?.length && downloadCredentialCsv(credentials)}
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: "10px" }}
          >
            Download CSV
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StoreManagementPanel;
